import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  CheckCircle,
  ArrowRight,
  Lightbulb,
  Target,
  Users,
  TrendingUp,
  Settings,
  Rocket,
} from 'lucide-react';

const DigitalTransformationPage = () => {
  const { t } = useTranslation();

  const services = [
    {
      icon: Lightbulb,
      title: t('dtPage.s1Title'),
      description: t('dtPage.s1Desc'),
      deliverables: [t('dtPage.s1d1'), t('dtPage.s1d2'), t('dtPage.s1d3'), t('dtPage.s1d4')],
    },
    {
      icon: Target,
      title: t('dtPage.s2Title'),
      description: t('dtPage.s2Desc'),
      deliverables: [t('dtPage.s2d1'), t('dtPage.s2d2'), t('dtPage.s2d3'), t('dtPage.s2d4')],
    },
    {
      icon: Settings,
      title: t('dtPage.s3Title'),
      description: t('dtPage.s3Desc'),
      deliverables: [t('dtPage.s3d1'), t('dtPage.s3d2'), t('dtPage.s3d3'), t('dtPage.s3d4')],
    },
  ];

  const methodology = [
    {
      phase: t('dtPage.p1Phase'),
      duration: t('dtPage.p1Duration'),
      activities: [t('dtPage.p1a1'), t('dtPage.p1a2'), t('dtPage.p1a3'), t('dtPage.p1a4')],
    },
    {
      phase: t('dtPage.p2Phase'),
      duration: t('dtPage.p2Duration'),
      activities: [t('dtPage.p2a1'), t('dtPage.p2a2'), t('dtPage.p2a3'), t('dtPage.p2a4')],
    },
    {
      phase: t('dtPage.p3Phase'),
      duration: t('dtPage.p3Duration'),
      activities: [t('dtPage.p3a1'), t('dtPage.p3a2'), t('dtPage.p3a3'), t('dtPage.p3a4')],
    },
    {
      phase: t('dtPage.p4Phase'),
      duration: t('dtPage.p4Duration'),
      activities: [t('dtPage.p4a1'), t('dtPage.p4a2'), t('dtPage.p4a3'), t('dtPage.p4a4')],
    },
  ];

  const caseStudies = [
    {
      company: t('dtPage.cs1Company'),
      industry: t('dtPage.cs1Industry'),
      challenge: t('dtPage.cs1Challenge'),
      solution: t('dtPage.cs1Solution'),
      results: [t('dtPage.cs1r1'), t('dtPage.cs1r2'), t('dtPage.cs1r3'), t('dtPage.cs1r4')],
    },
    {
      company: t('dtPage.cs2Company'),
      industry: t('dtPage.cs2Industry'),
      challenge: t('dtPage.cs2Challenge'),
      solution: t('dtPage.cs2Solution'),
      results: [t('dtPage.cs2r1'), t('dtPage.cs2r2'), t('dtPage.cs2r3'), t('dtPage.cs2r4')],
    },
    {
      company: t('dtPage.cs3Company'),
      industry: t('dtPage.cs3Industry'),
      challenge: t('dtPage.cs3Challenge'),
      solution: t('dtPage.cs3Solution'),
      results: [t('dtPage.cs3r1'), t('dtPage.cs3r2'), t('dtPage.cs3r3'), t('dtPage.cs3r4')],
    },
  ];

  return (
    <>
      <Helmet>
        <title>Digital Transformation Consulting | mwbTechs</title>
        <meta
          name="description"
          content="Transform your business with our digital transformation consulting. Strategy, implementation, and optimization for sustainable growth."
        />
      </Helmet>

      <div className="pt-20 min-h-screen bg-white">
        {/* Hero Section */}
        <section className="section-sm bg-gradient-to-br from-primary-50 to-blue-50">
          <div className="container-custom">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="inline-flex items-center gap-2 bg-primary-100 text-primary-700 px-4 py-2 rounded-full mb-6">
                  <Rocket className="w-4 h-4" />
                  <span className="font-medium">{t('dtPage.badge')}</span>
                </div>
                <h1 className="text-5xl font-bold text-gray-900 mb-6">
                  {t('dtPage.heroTitle')}
                </h1>
                <p className="text-xl text-gray-600 mb-8">
                  {t('dtPage.heroSubtitle')}
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link to="/contact" className="btn btn-primary text-lg">
                    {t('dtPage.scheduleConsultation')}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                  <a href="#methodology" className="btn btn-outline text-lg">
                    {t('dtPage.ourApproach')}
                  </a>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="grid grid-cols-2 gap-6"
              >
                {[
                  { value: '98%',  label: t('dtPage.stat2Label') },
                  { value: '15+',  label: t('dtPage.stat4Label') },
                ].map((stat, index) => (
                  <div key={index} className="bg-white rounded-xl p-6 shadow-lg text-center">
                    <div className="text-4xl font-bold text-primary-600 mb-2">{stat.value}</div>
                    <div className="text-sm text-gray-600">{stat.label}</div>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="section">
          <div className="container-custom">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('dtPage.servicesTitle')}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {t('dtPage.servicesSubtitle')}
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {services.map((service, index) => {
                const Icon = service.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-white rounded-xl p-8 border border-gray-200 shadow-sm hover:shadow-lg transition-all"
                  >
                    <div className="w-14 h-14 bg-primary-100 rounded-xl flex items-center justify-center mb-6">
                      <Icon className="w-7 h-7 text-primary-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{service.title}</h3>
                    <p className="text-gray-600 mb-6">{service.description}</p>
                    <div className="space-y-2">
                      <div className="text-sm font-semibold text-gray-700 mb-3">{t('dtPage.keyDeliverables')}</div>
                      {service.deliverables.map((item, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-600">{item}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Methodology Section */}
        <section id="methodology" className="section bg-gray-50">
          <div className="container-custom">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('dtPage.methodologyTitle')}
              </h2>
              <p className="text-xl text-gray-600">
                {t('dtPage.methodologySubtitle')}
              </p>
            </div>

            <div className="max-w-5xl mx-auto space-y-6">
              {methodology.map((phase, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl p-8 shadow-sm"
                >
                  <div className="flex flex-wrap items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-primary-600 text-white rounded-full flex items-center justify-center text-xl font-bold">
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-[200px]">
                      <h3 className="text-2xl font-bold text-gray-900">{phase.phase}</h3>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 ml-16">
                    {phase.activities.map((activity, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{activity}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Case Studies */}
        <section className="section">
          <div className="container-custom">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('dtPage.caseStudiesTitle')}
              </h2>
              <p className="text-xl text-gray-600">
                {t('dtPage.caseStudiesSubtitle')}
              </p>
            </div>

            <div className="space-y-8">
              {caseStudies.map((study, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl p-8 shadow-lg border border-gray-200"
                >
                  <div className="flex flex-wrap items-center gap-4 mb-6">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">{study.company}</h3>
                      <span className="inline-block bg-primary-100 text-primary-700 px-3 py-1 rounded-full text-sm font-medium mt-2">
                        {study.industry}
                      </span>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-6 mb-6">
                    <div>
                      <div className="text-sm font-semibold text-gray-500 mb-2">{t('dtPage.challenge')}</div>
                      <p className="text-gray-700">{study.challenge}</p>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-500 mb-2">{t('dtPage.solution')}</div>
                      <p className="text-gray-700">{study.solution}</p>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-500 mb-2">{t('dtPage.results')}</div>
                      <div className="space-y-2">
                        {study.results.map((result, i) => (
                          <div key={i} className="flex items-start gap-2">
                            <TrendingUp className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                            <span className="text-sm font-medium text-green-700">{result}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section-sm bg-gradient-primary text-white">
          <div className="container-custom text-center">
            <h2 className="text-4xl font-bold mb-4">
              {t('dtPage.ctaTitle')}
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              {t('dtPage.ctaSubtitle')}
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link to="/contact" className="btn btn-white text-lg">
                {t('dtPage.scheduleFreeConsultation')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mt-16 pt-16 border-t border-blue-400">
              <div className="text-center">
                <Users className="w-12 h-12 mx-auto mb-4 text-blue-200" />
                <div className="text-blue-100">{t('dtPage.expertConsultants')}</div>
              </div>
              <div className="text-center">
                <Target className="w-12 h-12 mx-auto mb-4 text-blue-200" />
                <div className="text-3xl font-bold mb-2">100%</div>
                <div className="text-blue-100">{t('dtPage.successRate')}</div>
              </div>
              <div className="text-center">
                <TrendingUp className="w-12 h-12 mx-auto mb-4 text-blue-200" />
                <div className="text-blue-100">{t('dtPage.avgValueCreated')}</div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default DigitalTransformationPage;
